import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import ManagementOpinionFilters from '../ManagementOpinionFilters'
import { usePersonalAreaContext } from '../PersonalAreaContext'
import * as R from 'ramda'

import styles from '../PersonalAreaController.module.sass'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { useEffect, useMemo, useState } from 'react'
import ManagementOpinionRow from 'components/cards/ManagementOpinionRow/ManagementOpinionRow'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import ReactMarkdown from 'react-markdown'
import { useDrawerContext } from 'context/Drawer/DrawerContext'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useSetRecoilState } from 'recoil'
import { openedTask } from 'store'

const byMedicalOpinionResolved = R.groupBy(function (opinion) {
	const isMedicalOpinionResolved = opinion?.isMedicalOpinionResolved
	return isMedicalOpinionResolved
		? `resolved management opinions`
		: `pending management opinions`
})

const mapIntoOpinionResolvedPairs = R.pipe(
	byMedicalOpinionResolved,
	R.toPairs,
	R.map(([title, value]) => ({ title, value })),
	R.sortWith([R.ascend(R.prop('title'))])
)

const ManagementOpinionController = () => {
	const { i18n } = useGlobal()
	const history = useHistory()
	const { managementOpinions, isLoading } = usePersonalAreaContext()
	const { managementOpinionsContent, tasksContent } = managementOpinions
	const { openDrawer } = useDrawerContext()
	const [dataCollection, setDataCollection] = useState([])
	const [searchTermQuery, setSearchTermQuery] = useState([])

	const setOpenTask = useSetRecoilState(openedTask)

	const labels = {
		noContentMessage: searchTermQuery
			? i18n`There are no **matches**.`
			: i18n`There are no **management opinions**.`,
	}

	const handleDataCollectionWithTermSearch = (
		childData,
		childSearchTermQuery
	) => {
		const opinionsGroupedByResolvedValuePairsWithSearchMatches =
			mapIntoOpinionResolvedPairs(childData?.matches || [])
		setDataCollection(opinionsGroupedByResolvedValuePairsWithSearchMatches)
		setSearchTermQuery(childSearchTermQuery?.query)
	}

	const opinionWithTasks = useMemo(
		() =>
			(managementOpinionsContent || []).map((managementOpinion) => {
				const taskForOpinion = tasksContent.find((task) => {
					return (
						task.opinionRequest?.opinionId === managementOpinion.id
					)
				})

				if (taskForOpinion) {
					return {
						...managementOpinion,
						task: taskForOpinion,
					}
				}

				return {
					...managementOpinion,
				}
			}),
		[managementOpinionsContent, tasksContent]
	)

	useEffect(() => {
		const opinionsGroupedByResolvedValue = mapIntoOpinionResolvedPairs(
			opinionWithTasks || []
		)
		setDataCollection(opinionsGroupedByResolvedValue)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [opinionWithTasks])

	const navigateToTask = (item) => {
		history.push(
			item.task?.id
				? `/clinical-processes/${item.processId}/appointments?taskId=${item.task?.id}`
				: `/clinical-processes/${item.processId}/appointments`
		)

		setOpenTask((prev) => {
			const nextTaskState =
				prev?.openTask?.id !== item.task?.id
					? {
							...prev,
							openTask: item.task,
					  }
					: prev

			return nextTaskState
		})
	}

	return (
		<>
			<Widget
				paddings={['small', 'normal', '']}
				applyMaxWidth
				className={styles['filters-widget']}
			>
				<ManagementOpinionFilters
					handleDataCollection={handleDataCollectionWithTermSearch}
					data={opinionWithTasks}
					className={styles['filters-options']}
				/>
			</Widget>

			{!isLoading && !dataCollection.length ? (
				<ReactMarkdown
					className={styles['no-opinions']}
					allowedTypes={[
						'break',
						'paragraph',
						'strong',
						'text',
						'list',
						'listItem',
						'code',
					]}
					unwrapDisallowed={true}
				>
					{labels.noContentMessage}
				</ReactMarkdown>
			) : (
				dataCollection.map((item, idx) => (
					<Widget
						key={idx}
						paddings={['', '', '', '']}
						applyMaxWidth
						margins={['small', 'large', 'xsmall', 'large']}
					>
						<ExpandableWidget
							className={styles['today-widget']}
							titleAppearance='no-line'
							expanded={true}
							heading='h4'
							title={i18n.translate(item?.title)}
							borderRadius
						>
							<VStack className={`${styles['content']}`}>
								{!isLoading && !dataCollection?.length ? (
									<ReactMarkdown
										className={styles['no-reminders']}
										allowedTypes={[
											'break',
											'paragraph',
											'strong',
											'text',
											'list',
											'listItem',
											'code',
										]}
										unwrapDisallowed={true}
									>
										{labels.noContentMessage}
									</ReactMarkdown>
								) : (
									<>
										{(item?.value || []).map(
											(item, idx) => (
												<ManagementOpinionRow
													key={idx}
													item={item}
													action={() => {
														item.isMedicalOpinionResolved
															? openDrawer(
																	'ManagementOpinionDrawer',
																	item,
																	`/clinical-processes/${item.processId}`
															  )
															: navigateToTask(
																	item
															  )
													}}
												/>
											)
										)}
									</>
								)}
							</VStack>
						</ExpandableWidget>
					</Widget>
				))
			)}
		</>
	)
}

export default ManagementOpinionController
